import React, { Dispatch, SetStateAction, useEffect, useRef } from "react";
// import { useSearchParams } from "@tanstack/react-router";
import { useInitialLoadContext } from "@/contexts/InitialLoadContext";

export const useStateSyncedWithQueryParam = <StateType, >(
  queryParamKey: string,
  defaultValue: StateType,
  toString: (val: StateType) => string,
  fromString: (val: string | null) => StateType | undefined
): [StateType, Dispatch<SetStateAction<StateType>>] => {

  // const [searchParams, setSearchParams] = useSearchParams();
  const searchParams = { get: (foo: any) => {} }
  const setSearchParams = (foo:any) => {};

  const [state, setState] = React.useState<StateType>(() => {
    const paramValue = searchParams.get(queryParamKey);
    const match = fromString(paramValue);
    if (!paramValue) {
      return defaultValue
    }
    return match || defaultValue;
  });

  useEffect(() => {
    if (searchParams.get(queryParamKey) !== toString(state)) {
      setSearchParams(
        (prev) => {
          prev.delete(queryParamKey)
          prev.set(queryParamKey, toString(state))
          return prev;
        },
        { replace: true }
      );
    }
  }, [state, searchParams]);

  return [state, setState]
}

export const useInitialOverrideUnlessInQueryParamsOnFirstLoad = <ValueType, >(
  queryParamKey: string,
  overrideValue: ValueType | undefined,
  setState: Dispatch<SetStateAction<ValueType>>
) => {
  const overrideValueRef = useRef<ValueType | undefined>(overrideValue);

  const { initialPageLoadSearchParams, hasNavigatedAwayFromInitialLocation } = useInitialLoadContext();

  useEffect(() => {
    if (overrideValueRef.current) {
      const shouldOverride = hasNavigatedAwayFromInitialLocation.current || !initialPageLoadSearchParams?.has(queryParamKey);
      if (shouldOverride) {
        setState(overrideValueRef.current)
      }
      overrideValueRef.current = undefined;
    }
  }, [overrideValueRef, initialPageLoadSearchParams, hasNavigatedAwayFromInitialLocation]);

  return overrideValueRef.current
}
